import { AttachmentRule, Behavior } from "./types";
import * as RAPIER from "@dimforge/rapier3d-compat";

export class RapierSystemBehavior extends Behavior {
  private static instance: RapierSystemBehavior | null = null;
  private static physicsWorld: RAPIER.World | null = null;
  private static isInitialized: boolean = false;

  static getAttachmentRules(): AttachmentRule[] {
    return [{ type: "root", selector: "" }];
  }

  // Singleton pattern - only one physics system per world
  static getInstance(): RapierSystemBehavior | null {
    return RapierSystemBehavior.instance;
  }

  static getPhysicsWorld(): RAPIER.World | null {
    return RapierSystemBehavior.physicsWorld;
  }

  onAttach(): void {
    // Ensure only one instance exists
    if (RapierSystemBehavior.instance) {
      console.warn(
        "RapierSystemBehavior: Only one physics system can exist. Ignoring duplicate attachment.",
      );
      return;
    }

    console.log("RapierSystemBehavior: Initializing physics system...");
    RapierSystemBehavior.instance = this;

    this.initializePhysics();
  }

  onDetach(): void {
    console.log("RapierSystemBehavior: Shutting down physics system...");

    if (RapierSystemBehavior.physicsWorld) {
      RapierSystemBehavior.physicsWorld.free();
      RapierSystemBehavior.physicsWorld = null;
    }

    RapierSystemBehavior.instance = null;
    RapierSystemBehavior.isInitialized = false;
  }

  private async initializePhysics(): Promise<void> {
    try {
      await RAPIER.init();

      // Read gravity from scene attributes or use default
      const gravityX = parseFloat(this.element.getAttribute("gravity-x") || "0.0");
      const gravityY = parseFloat(this.element.getAttribute("gravity-y") || "-9.81");
      const gravityZ = parseFloat(this.element.getAttribute("gravity-z") || "0.0");

      const gravity = { x: gravityX, y: gravityY, z: gravityZ };
      RapierSystemBehavior.physicsWorld = new RAPIER.World(gravity);
      RapierSystemBehavior.physicsWorld.integrationParameters.dt = 0.05;

      // Create the ground plane by default
      //this.createDefaultGround();

      RapierSystemBehavior.isInitialized = true;
      console.log("RapierSystemBehavior: Physics world initialized with gravity:", gravity);
    } catch (error) {
      console.error("RapierSystemBehavior: Failed to initialize physics:", error);
    }
  }

  private createDefaultGround(): void {
    if (!RapierSystemBehavior.physicsWorld) return;

    // Read ground configuration from scene attributes
    const groundWidth = parseFloat(this.element.getAttribute("ground-width") || "150.0");
    const groundHeight = parseFloat(this.element.getAttribute("ground-height") || "1.0");
    const groundDepth = parseFloat(this.element.getAttribute("ground-depth") || "150.0");
    const groundY = parseFloat(this.element.getAttribute("ground-y") || "5.0");

    // Create ground collider
    const groundColliderDesc = RAPIER.ColliderDesc.cuboid(
      groundWidth / 2,
      groundHeight / 2,
      groundDepth / 2,
    ).setTranslation(0.0, groundY - groundHeight / 2, 0.0);

    RapierSystemBehavior.physicsWorld.createCollider(groundColliderDesc);
    console.log("RapierSystemBehavior: Default ground created");
  }

  tick(deltaTime: number): void {
    if (!RapierSystemBehavior.physicsWorld || !RapierSystemBehavior.isInitialized) {
      return;
    }

    // Step the physics simulation
    RapierSystemBehavior.physicsWorld.step();

    // Handle gravity changes
    this.updateGravity();
  }

  private updateGravity(): void {
    if (!RapierSystemBehavior.physicsWorld) return;

    const gravityX = parseFloat(this.element.getAttribute("gravity-x") || "0.0");
    const gravityY = parseFloat(this.element.getAttribute("gravity-y") || "-9.81");
    const gravityZ = parseFloat(this.element.getAttribute("gravity-z") || "0.0");

    const currentGravity = RapierSystemBehavior.physicsWorld.gravity;

    // Only update if gravity has changed
    if (
      currentGravity.x !== gravityX ||
      currentGravity.y !== gravityY ||
      currentGravity.z !== gravityZ
    ) {
      RapierSystemBehavior.physicsWorld.gravity = { x: gravityX, y: gravityY, z: gravityZ };
      console.log("RapierSystemBehavior: Gravity updated to:", {
        x: gravityX,
        y: gravityY,
        z: gravityZ,
      });
    }
  }

  onAttributeChange(attributeName: string, oldValue: string | null, newValue: string | null): void {
    // Handle changes to physics-related attributes
    if (attributeName.startsWith("gravity-")) {
      this.updateGravity();
    }
  }

  // Static utility methods for other behaviors to interact with physics
  static createRigidBody(desc: RAPIER.RigidBodyDesc): RAPIER.RigidBody | null {
    if (!RapierSystemBehavior.physicsWorld) {
      console.warn("RapierSystemBehavior: Physics world not initialized");
      return null;
    }
    return RapierSystemBehavior.physicsWorld.createRigidBody(desc);
  }

  static createCollider(
    desc: RAPIER.ColliderDesc,
    rigidBody?: RAPIER.RigidBody,
  ): RAPIER.Collider | null {
    if (!RapierSystemBehavior.physicsWorld) {
      console.warn("RapierSystemBehavior: Physics world not initialized");
      return null;
    }
    return RapierSystemBehavior.physicsWorld.createCollider(desc, rigidBody);
  }

  static createStaticCollider(desc: RAPIER.ColliderDesc): RAPIER.Collider | null {
    if (!RapierSystemBehavior.physicsWorld) {
      console.warn("RapierSystemBehavior: Physics world not initialized");
      return null;
    }
    // Create a collider without attaching it to any rigidbody (static collider)
    return RapierSystemBehavior.physicsWorld.createCollider(desc);
  }

  static removeRigidBody(rigidBody: RAPIER.RigidBody): void {
    if (!RapierSystemBehavior.physicsWorld) {
      console.warn("RapierSystemBehavior: Physics world not initialized");
      return;
    }
    RapierSystemBehavior.physicsWorld.removeRigidBody(rigidBody);
  }

  static removeCollider(collider: RAPIER.Collider): void {
    if (!RapierSystemBehavior.physicsWorld) {
      console.warn("RapierSystemBehavior: Physics world not initialized");
      return;
    }
    RapierSystemBehavior.physicsWorld.removeCollider(collider, true);
  }
}
